import { wizard } from "@msys/question-isomorphic";
import { Slider, Stack, TextField, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { PropertyOptionItem } from "../../doc-items/PropertyOptionItem.js";
import { fitValueNumberToRangeStep } from "../../doc-items/properties.js";
import { WizardQuestions_Question_WizardMultiplePropertiesQuestion_Fragment } from "../../documents/WizardQuestions.generated.js";
import { PropertyAnswerEntry, propertiesAction } from "./answerToItemAction.js";
import { askIfFromQuestion } from "./askIfContext.js";
import {
  WizardRendererProps,
  WizardRendererRef,
} from "./WizardRendererProps.js";
import { WizardTileGrid } from "./WizardTileGrid.js";

type QuestionProp =
  WizardQuestions_Question_WizardMultiplePropertiesQuestion_Fragment["props"][number]["prop"];

type PropValue = PropertyAnswerEntry["value"];

type TileMedia = NonNullable<
  Extract<
    QuestionProp,
    { __typename: "Props2Text" }
  >["allowedValuesText"][number]["media"]
>;

/**
 * The width a free input gets, matching the end-customer wizard's `w-60`.
 *
 * The MUI theme defaults `MuiTextField` to `fullWidth`, which is right for a
 * form column but wrong here: a property field stretched across the whole
 * question panel reads as a text area rather than a value to fill in. The
 * theme's `filled` variant is deliberately left alone — that is where the
 * light-blue field comes from.
 */
const FIELD_WIDTH = 240;

/**
 * A property group, laid out the way the end-customer wizard lays it out: one
 * property per block, and a property with allowed values offered as pickable
 * tiles rather than a dropdown — the value's picture is most of what the user
 * is choosing by.
 *
 * Where the property is a plain field, its name sits *inside* the field, in
 * the filled variant's own label row. A block heading is then added only for
 * a prompt that says something the name does not — otherwise the same words
 * would appear twice, and the field would reserve a label row it never uses.
 * Tiles have nowhere to put a name, so they always carry the heading.
 *
 * There is no submit button of its own. The action bar's forward button submits
 * through the ref below, so a question has exactly one way onward.
 *
 * Only the props visible for the staged values are rendered, re-evaluated on
 * every change from the query's ask-if context; every answered prop is
 * submitted, hidden or not.
 */
export const WizardPropertiesRenderer = React.forwardRef<
  WizardRendererRef,
  WizardRendererProps<WizardQuestions_Question_WizardMultiplePropertiesQuestion_Fragment> & {
    /** Set where the surrounding view already names the group. */
    hideGroupHeading?: boolean;
  }
>(({ question, onAnswer, disabled, hideGroupHeading = false }, ref) => {
  const { t } = useTranslate(["Global"]);

  const props = React.useMemo(
    () => question.props.map(p => p.prop),
    [question.props]
  );
  const askIf = React.useMemo(() => askIfFromQuestion(question), [question]);

  const initial = React.useMemo(
    () => Object.fromEntries(props.map(p => [p.key, currentValue(p)])),
    [props]
  );
  const [values, setValues] =
    React.useState<Record<string, PropValue>>(initial);

  React.useEffect(() => setValues(initial), [initial]);

  const visibleFor = React.useCallback(
    (staged: Record<string, PropValue>) => {
      const visibleKeys = wizard.askIf.visiblePropKeys({
        itemId: question.itemId,
        props,
        askIf,
        staged: props.map(prop => entryFor(prop, staged[prop.key])),
      });
      return props.filter(prop => visibleKeys.includes(prop.key));
    },
    [askIf, props, question.itemId]
  );

  const visibleProps = React.useMemo(
    () => visibleFor(values),
    [visibleFor, values]
  );

  const submit = React.useCallback(
    (override?: Record<string, PropValue>) => {
      const submitted = override ?? values;

      return onAnswer(
        propertiesAction(
          question.itemId,
          props.map(prop => entryFor(prop, submitted[prop.key]))
        )
      );
    },
    [onAnswer, props, question.itemId, values]
  );

  React.useImperativeHandle(ref, () => ({ submit: () => void submit() }), [
    submit,
  ]);

  const change = (prop: QuestionProp, value: PropValue) => {
    const next = { ...values, [prop.key]: value };
    setValues(next);

    // A single-property question is the whole answer, so picking its value is
    // the answer — unless the pick reveals another prop to ask.
    if (
      wizard.askIf.autoSubmits({
        visibleAfterChange: visibleFor(next).map(p => p.key),
        changed: { key: prop.key, pickable: isPickable(prop), value },
      })
    )
      void submit(next);
  };

  return (
    <Stack direction="column" spacing={4}>
      {groupProps(visibleProps).map(([group, props]) => (
        <Stack key={group || "__ungrouped"} direction="column" spacing={3}>
          {group && !hideGroupHeading && (
            <Typography variant="overline" color="text.secondary">
              {group}
            </Typography>
          )}
          {props.map(prop => {
            const heading = headingFor(prop);

            return (
              <Stack
                key={prop.key}
                direction="column"
                spacing={1}
                sx={{ alignItems: "flex-start" }}
              >
                {heading && (
                  <Typography variant="subtitle1">{heading}</Typography>
                )}
                <PropField
                  prop={prop}
                  value={values[prop.key]}
                  disabled={disabled}
                  onChange={value => change(prop, value)}
                  yesLabel={t("Yes", { ns: "Global" })}
                  noLabel={t("No", { ns: "Global" })}
                />
              </Stack>
            );
          })}
        </Stack>
      ))}
    </Stack>
  );
});

const PropField = ({
  prop,
  value,
  disabled,
  onChange,
  yesLabel,
  noLabel,
}: {
  prop: QuestionProp;
  value: PropValue;
  disabled: boolean;
  onChange: (value: PropValue) => void;
  yesLabel: string;
  noLabel: string;
}) => {
  const label = prop.label || prop.key;

  switch (prop.__typename) {
    case "Props2Bool": {
      const mediaFor = (bool: boolean) =>
        prop.valuesMediaBool.find(m => m.bool === bool)?.media ?? null;

      return (
        <Tiles
          label={label}
          disabled={disabled}
          value={typeof value === "boolean" ? String(value) : null}
          onChange={next => onChange(next === null ? null : next === "true")}
          options={[
            { value: "true", label: yesLabel, media: mediaFor(true) },
            { value: "false", label: noLabel, media: mediaFor(false) },
          ]}
        />
      );
    }
    case "Props2Number": {
      if (prop.allowedValuesNumber.length > 0) {
        return (
          <Tiles
            label={label}
            disabled={disabled}
            value={typeof value === "number" ? String(value) : null}
            onChange={next => onChange(next === null ? null : Number(next))}
            options={prop.allowedValuesNumber.map(a => ({
              value: String(a.allowedNumber),
              label: withUnit(a.allowedNumber, prop.unit),
              media: a.media ?? null,
            }))}
          />
        );
      }

      const range = prop.range;
      const min = range?.min ?? null;
      const max = range?.max ?? null;

      return (
        <Stack direction="column" spacing={1} sx={{ width: FIELD_WIDTH }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <TextField
              label={label}
              type="number"
              fullWidth
              disabled={disabled}
              value={typeof value === "number" ? String(value) : ""}
              onChange={event =>
                onChange(
                  event.target.value === "" ? null : Number(event.target.value)
                )
              }
              onBlur={() => {
                if (typeof value !== "number") return;
                const fitted = fitValueNumberToRangeStep(
                  value,
                  prop.range,
                  prop.step
                );
                if (fitted !== value) onChange(fitted);
              }}
            />
            {prop.unit && (
              <Typography variant="body2" color="text.secondary">
                {prop.unit}
              </Typography>
            )}
          </Stack>
          {min !== null && max !== null && (
            <Slider
              size="small"
              disabled={disabled}
              min={min}
              max={max}
              step={prop.step ?? undefined}
              value={typeof value === "number" ? value : min}
              onChange={(_, next) =>
                onChange(Array.isArray(next) ? (next[0] ?? null) : next)
              }
              valueLabelDisplay="auto"
              aria-label={label}
            />
          )}
        </Stack>
      );
    }
    case "Props2Text": {
      if (prop.allowedValuesText.length > 0) {
        return (
          <Tiles
            label={label}
            disabled={disabled}
            value={typeof value === "string" ? value : null}
            onChange={onChange}
            options={prop.allowedValuesText.map(a => ({
              value: a.allowedText,
              label: a.allowedText,
              media: a.media ?? null,
            }))}
          />
        );
      }

      return (
        <TextField
          label={label}
          fullWidth={false}
          sx={{ width: FIELD_WIDTH }}
          disabled={disabled}
          value={typeof value === "string" ? value : ""}
          onChange={event => onChange(event.target.value)}
        />
      );
    }
    case "Props2TextArray": {
      // A multi-value property with nothing to pick from has no control in the
      // end-customer wizard either. Offering an empty tile group would be a
      // visible dead end; how to enter free multi-values is an open question
      // there and is not one to answer here.
      if (prop.allowedValuesText.length === 0) return null;

      return (
        <Tiles
          label={label}
          multiple
          disabled={disabled}
          value={Array.isArray(value) ? (value as Array<string>) : []}
          onChange={next => onChange(next)}
          options={prop.allowedValuesText.map(a => ({
            value: a.allowedText,
            label: a.allowedText,
            media: a.media ?? null,
          }))}
        />
      );
    }
    case "Props2NumberArray": {
      if (prop.allowedValuesNumber.length === 0) return null;

      return (
        <Tiles
          label={label}
          multiple
          disabled={disabled}
          value={
            Array.isArray(value) ? (value as Array<number>).map(String) : []
          }
          onChange={next => onChange(next.map(Number))}
          options={prop.allowedValuesNumber.map(a => ({
            value: String(a.allowedNumber),
            label: withUnit(a.allowedNumber, prop.unit),
            media: a.media ?? null,
          }))}
        />
      );
    }
  }
};

type TileOption = { value: string; label: string; media: TileMedia | null };

/**
 * The allowed values of a property as pickable tiles, single- or multi-select.
 * The tile is the one the in-app property forms use, so a value's picture is
 * the tile's face and the whole tile is the click target — the same weight the
 * end-customer wizard gives an option. Once any value has a picture, every tile
 * reserves the picture slot, so the row stays level.
 */
function Tiles(
  props: {
    label: string;
    disabled: boolean;
    options: Array<TileOption>;
  } & (
    | {
        multiple?: false;
        value: string | null;
        onChange: (v: string | null) => void;
      }
    | {
        multiple: true;
        value: Array<string>;
        onChange: (v: Array<string>) => void;
      }
  )
) {
  const hasMedia = props.options.some(o => o.media);

  const toggle = (optionValue: string, selected: boolean) => {
    if (props.multiple) {
      props.onChange(
        selected
          ? [...props.value, optionValue]
          : props.value.filter(value => value !== optionValue)
      );
    } else {
      props.onChange(selected ? optionValue : null);
    }
  };

  return (
    <WizardTileGrid label={props.label}>
      {props.options.map(option => {
        const selected = props.multiple
          ? props.value.includes(option.value)
          : props.value === option.value;

        return (
          <PropertyOptionItem
            key={option.value}
            label={option.label}
            isPreselected={selected}
            isGreyedOut={!selected}
            image={option.media}
            withImage={hasMedia}
            multiple={props.multiple}
            disabled={props.disabled}
            onClick={() => toggle(option.value, !selected)}
          />
        );
      })}
    </WizardTileGrid>
  );
}

/**
 * The block heading, or `null` when the field's own label already carries the
 * property's name. A prompt is authored per item and is often a sentence, so
 * it stays a heading rather than being squeezed into a label row.
 */
function headingFor(prop: QuestionProp): string | null {
  const name = prop.label || prop.key;

  if (!rendersOwnLabel(prop)) return prop.prompt || name;

  return prop.prompt && prop.prompt !== name ? prop.prompt : null;
}

/** Whether the property renders as a field, which holds its own label. */
function rendersOwnLabel(prop: QuestionProp): boolean {
  switch (prop.__typename) {
    case "Props2Text":
      return prop.allowedValuesText.length === 0;
    case "Props2Number":
      return prop.allowedValuesNumber.length === 0;
    case "Props2Bool":
    case "Props2TextArray":
    case "Props2NumberArray":
      return false;
  }
}

/** Whether answering this property is a single click rather than typing. */
function isPickable(prop: QuestionProp): boolean {
  switch (prop.__typename) {
    case "Props2Bool":
      return true;
    case "Props2Number":
      return prop.allowedValuesNumber.length > 0;
    case "Props2Text":
      return prop.allowedValuesText.length > 0;
    case "Props2TextArray":
    case "Props2NumberArray":
      // Multi-select is never finished by one click.
      return false;
  }
}

/** A tile's label carries its unit inline; a field shows it beside the input. */
function withUnit(value: string | number, unit: string | null | undefined) {
  return unit ? `${value} ${unit}` : String(value);
}

/** Groups keep the query's order; the ungrouped block stays first. */
function groupProps(
  props: Array<QuestionProp>
): Array<[string, Array<QuestionProp>]> {
  const groups = new Map<string, Array<QuestionProp>>();

  for (const prop of props) {
    const existing = groups.get(prop.group);
    if (existing) existing.push(prop);
    else groups.set(prop.group, [prop]);
  }

  return [...groups.entries()];
}

function currentValue(prop: QuestionProp): PropValue {
  switch (prop.__typename) {
    case "Props2Bool":
      return prop.valueBool ?? null;
    case "Props2Number":
      return prop.valueNumber ?? null;
    case "Props2Text":
      return prop.valueText ?? null;
    case "Props2TextArray":
      return prop.valueTextArray ?? null;
    case "Props2NumberArray":
      return prop.valueNumberArray ?? null;
  }
}

function entryFor(prop: QuestionProp, value: PropValue): PropertyAnswerEntry {
  switch (prop.__typename) {
    case "Props2Bool":
      return {
        kind: "Props2Bool",
        key: prop.key,
        value: typeof value === "boolean" ? value : null,
      };
    case "Props2Number":
      // The forward button can submit without the field ever blurring.
      return {
        kind: "Props2Number",
        key: prop.key,
        value:
          typeof value === "number"
            ? fitValueNumberToRangeStep(value, prop.range, prop.step)
            : null,
      };
    case "Props2Text":
      return {
        kind: "Props2Text",
        key: prop.key,
        value: typeof value === "string" ? value : null,
      };
    case "Props2TextArray":
      return {
        kind: "Props2TextArray",
        key: prop.key,
        value: Array.isArray(value) ? (value as Array<string>) : null,
      };
    case "Props2NumberArray":
      return {
        kind: "Props2NumberArray",
        key: prop.key,
        value: Array.isArray(value) ? (value as Array<number>) : null,
      };
  }
}
